import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { MessageSquare, Send, X, Loader2, FileText, Info, Shield, Mail, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

export function LegalFooter({ screen }) {
  const { t } = useT();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  const sendFeedback = async (e) => {
      e.preventDefault();
      if (!feedbackText.trim()) return;
      
      setIsSending(true);

      try {
          const response = await fetch("https://api.web3forms.com/submit", {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
              },
              body: JSON.stringify({
                  access_key: "3ac5d72f-833d-4b9e-8e98-7b67669b85b9",
                  subject: "[FEEDBACK-APP] Zgłoszenie z gry Heads Up",
                  message: feedbackText,
                  from_name: "Gracz Heads Up Online"
              }),
          });

          if (response.ok) {
              toast.success("Zgłoszenie wysłane! Dziękujemy.");
              setFeedbackOpen(false);
              setFeedbackText("");
          } else {
              toast.error("Coś poszło nie tak. Spróbuj ponownie później.");
          }
      } catch (error) {
          toast.error("Błąd połączenia. Sprawdź swój internet.");
      } finally {
          setIsSending(false);
      }
  };

  const closeModal = () => setActiveModal(null);

  return (
    <>
      <div className="w-full bg-[#12121A] border-t border-white/5 py-4 px-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-medium text-[#A0A0B0] z-20 relative">
        <button onClick={() => setActiveModal('rules')} className="flex items-center gap-1.5 hover:text-white transition-colors">
           <FileText size={14} /> {t("footer_rules")}
        </button>
        <span className="opacity-30 hidden sm:inline">•</span>
        <button onClick={() => setActiveModal('about')} className="flex items-center gap-1.5 hover:text-white transition-colors">
           <Info size={14} /> {t("footer_about")}
        </button>
        <span className="opacity-30 hidden sm:inline">•</span>
        <button onClick={() => setActiveModal('privacy')} className="flex items-center gap-1.5 hover:text-white transition-colors">
           <Shield size={14} /> {t("footer_privacy")}
        </button>
        <span className="opacity-30 hidden sm:inline">•</span>
        <button onClick={() => setActiveModal('faq')} className="flex items-center gap-1.5 hover:text-white transition-colors">
           <HelpCircle size={14} /> {t("footer_faq") || "Pomoc / FAQ"}
        </button>
        <span className="opacity-30 hidden sm:inline">•</span>
        <button onClick={() => setActiveModal('contact')} className="flex items-center gap-1.5 hover:text-[#00FFFF] transition-colors">
           <Mail size={14} /> {t("footer_contact")}
        </button>
        <span className="opacity-30 hidden sm:inline">•</span>
        <button onClick={() => setFeedbackOpen(true)} className="flex items-center gap-1.5 text-[#00FFFF] hover:text-white transition-colors font-bold bg-[#00FFFF]/10 px-3 py-1.5 rounded-lg border border-[#00FFFF]/30">
          <MessageSquare size={14} /> Feedback
        </button>
      </div>

      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm pop-in" onClick={closeModal}>
          <div className="bg-[#12121A] border-2 border-white/10 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/5">
              <h2 className="font-display font-black text-2xl text-white">
                {activeModal === 'rules' && t("rules_title")}
                {activeModal === 'about' && t("about_title")}
                {activeModal === 'privacy' && t("privacy_title")}
                {activeModal === 'contact' && t("contact_title")}
                {activeModal === 'faq' && (t("faq_title") || "Rozwiązywanie problemów")}
              </h2>
              <button onClick={closeModal} className="p-2 bg-white/10 hover:bg-[#FF007F] text-white rounded-xl transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 sm:p-8 overflow-y-auto space-y-6 text-[#A0A0B0] text-sm leading-relaxed custom-scrollbar text-left">
              {activeModal === 'rules' && (
                <>
                  <p className="font-bold text-white text-base">{t("rules_intro")}</p>
                  <h3 className="text-[#00FFFF] font-bold text-lg mt-4">{t("rules_h1")}</h3>
                  <p>{t("rules_p1_1")}</p>
                  <p>{t("rules_p1_2")}</p>
                  <h3 className="text-[#00FFFF] font-bold text-lg mt-4">{t("rules_h2")}</h3>
                  <p><strong className="text-white">{t("rules_m1_t")}:</strong> {t("rules_m1_d")}</p>
                  <p><strong className="text-white">{t("rules_m2_t")}:</strong> {t("rules_m2_d")}</p>
                  <h3 className="text-[#00FFFF] font-bold text-lg mt-4">{t("rules_h3")}</h3>
                  <p>{t("rules_p3")}</p>
                  <h3 className="text-[#00FFFF] font-bold text-lg mt-4">{t("rules_h4")}</h3>
                  <p>{t("rules_p4")}</p>
                </>
              )}
              
              {activeModal === 'about' && (
                <>
                  <p className="font-bold text-white text-base">{t("about_intro")}</p>
                  <h3 className="text-[#FFE800] font-bold text-lg mt-4">{t("about_h1")}</h3>
                  <p>{t("about_p1_1")}</p>
                  <p>{t("about_p1_2")}</p>
                  <h3 className="text-[#FFE800] font-bold text-lg mt-4">{t("about_h2")}</h3>
                  <p>{t("about_p2")}</p>
                </>
              )}

              {activeModal === 'privacy' && (
                <>
                  <p className="font-bold text-white text-base">{t("privacy_intro")}</p>
                  <h3 className="text-[#00FF66] font-bold text-lg mt-4">{t("privacy_h1")}</h3>
                  <p>{t("privacy_p1")}</p>
                  <h3 className="text-[#00FF66] font-bold text-lg mt-4">{t("privacy_h2")}</h3>
                  <p>{t("privacy_p2")}</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>{t("privacy_l1")}</li>
                    <li>{t("privacy_l2")}</li>
                    <li>{t("privacy_l3")}</li>
                  </ul>
                  <h3 className="text-[#00FF66] font-bold text-lg mt-4">{t("privacy_h3")}</h3>
                  <p>{t("privacy_p3")}</p>
                  <h3 className="text-[#00FF66] font-bold text-lg mt-4">{t("privacy_h4")}</h3>
                  <p>{t("privacy_p4")}</p>
                </>
              )}

              {activeModal === 'contact' && (
                <>
                  <p className="font-bold text-white text-base">{t("contact_p1")}</p>
                  <p>{t("contact_p2")}</p>
                  <div className="mt-6 p-4 bg-white/5 border-2 border-white/10 rounded-2xl flex items-center justify-center gap-3">
                    <Mail className="text-[#FF007F]" size={24} />
                    <span className="font-bold text-white tracking-wide">
                      {t("contact_email")} <a href="mailto:headsuponline26@gmail.com" className="text-[#00FFFF] hover:underline">headsuponline26@gmail.com</a>
                    </span>
                  </div>
                </>
              )}

              {activeModal === 'faq' && (
                <>
                  <div className="p-4 bg-white/5 border-2 border-[#FFE800]/30 rounded-2xl">
                    <h3 className="text-[#FFE800] font-bold text-lg mb-2 flex items-center gap-2">
                      <HelpCircle size={18} /> {t("faq_q1") || "Nie mogę się zalogować / dołączyć"}
                    </h3>
                    <p className="text-white text-sm">
                      {t("faq_a1") || "Jeśli używasz przeglądarki Brave, wyłącz 'Brave Shields' (ikona lwa). Jeśli używasz blokerów sieciowych (Pi-hole), wyczyść cache DNS ('ipconfig /flushdns')."}
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="p-4 border-t border-white/10 flex justify-end bg-white/5">
               <button onClick={closeModal} className="px-6 py-2.5 rounded-xl bg-white/10 text-white font-bold hover:bg-white/20 transition-colors">
                  {t("legal_close") || "Zamknij"}
               </button>
            </div>
          </div>
        </div>
      )}

      {feedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm pop-in" onClick={() => !isSending && setFeedbackOpen(false)}>
          <div className="bg-[#12121A] border-2 border-[#00FFFF]/30 rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !isSending && setFeedbackOpen(false)} className="absolute top-4 right-4 text-[#A0A0B0] hover:text-white transition-colors">
               <X size={24} />
            </button>
            <h2 className="text-2xl font-black text-[#00FFFF] mb-2 flex items-center gap-2">
               <MessageSquare size={24} /> Zostaw Feedback
            </h2>
            <p className="text-sm text-[#A0A0B0] mb-6">Masz pomysł na rozwój gry? Znalazłeś błąd? Napisz do nas! Twoja opinia zostanie oznaczona w naszym systemie.</p>
            
            <form onSubmit={sendFeedback} className="space-y-4">
               <textarea 
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  disabled={isSending}
                  placeholder="Opisz swój pomysł lub problem..."
                  className="w-full h-32 bg-[#050510] border-2 border-white/10 rounded-xl p-4 text-white focus:outline-none focus:border-[#00FFFF] resize-none disabled:opacity-50"
               />
               <button type="submit" disabled={!feedbackText.trim() || isSending} className="w-full py-3 rounded-xl bg-[#00FFFF] text-[#050510] font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-transform hover:-translate-y-1">
                  {isSending ? (
                     <><Loader2 size={18} className="animate-spin" /> Wysyłanie...</>
                  ) : (
                     <><Send size={18} /> Wyślij Zgłoszenie</>
                  )}
               </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}